import React, { PureComponent } from 'react';
import type { Dispatch } from 'redux';

import { updateConferenceCallEnded } from '../../../base/conference';
import { disconnect } from '../../../base/connection';
import { translate } from '../../../base/i18n';
import { Icon, IconHangup } from '../../../base/icons';
import {
    getYoutubeParticipant,
    isLocalParticipantModerator,
    kickParticipant
} from '../../../base/participants';
import { connect } from '../../../base/redux';
import { verifyConferenceMeetingLimit } from '../../actions.web';
import { getSubscriptionPage } from '../../functions.web';

type Props = {
    _conference: Object,
    _isModerator: boolean,
    _meetingLimitDetails: Object,
    _remoteParticipants: Array<Object>,
    _rooms: Object,
    _youtubeParticipant: ?Object,
    dispatch: Dispatch<any>,
    t: Function
};

type State = {
    isLeaving: boolean,
    isReloading: boolean,
    recheckError: ?string
};

const styles = {
    card: {
        background: '#161d30',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.7)',
        padding: '36px 32px',
        maxWidth: '520px',
        width: '90%',
        textAlign: 'center',
        color: '#FFFFFF',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    iconWrapper: {
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        backgroundColor: 'rgba(237, 137, 54, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '20px'
    },
    title: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#FFFFFF',
        marginBottom: '14px'
    },
    description: {
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#CBD5E0',
        marginBottom: '24px'
    },
    buttonContainer: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        width: '100%',
        flexWrap: 'wrap'
    },
    primaryButton: {
        backgroundColor: '#3182CE',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'background-color 0.2s',
        minWidth: '150px'
    },
    secondaryButton: {
        backgroundColor: 'transparent',
        color: '#E2E8F0',
        border: '1px solid rgba(255, 255, 255, 0.25)',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.2s',
        minWidth: '130px'
    },
    secondaryButtonDisabled: {
        backgroundColor: 'transparent',
        color: '#E2E8F0',
        border: '1px solid rgba(255, 255, 255, 0.25)',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'not-allowed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.2s',
        minWidth: '130px',
        opacity: 0.7
    },
    spinnerIcon: {
        animation: 'spin 1s linear infinite'
    },
    dangerButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#E53E3E',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '11px 20px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 6px rgba(229, 62, 62, 0.3)'
    },
    dangerButtonDisabled: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        backgroundColor: '#9B2C2C',
        color: 'rgba(255, 255, 255, 0.7)',
        border: 'none',
        borderRadius: '8px',
        padding: '11px 20px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'not-allowed',
        boxShadow: 'none'
    },
    alertBadge: {
        backgroundColor: 'rgba(229, 62, 62, 0.2)',
        border: '1px solid rgba(229, 62, 62, 0.4)',
        borderRadius: '8px',
        padding: '10px 16px',
        color: '#FEB2B2',
        fontSize: '13px',
        marginBottom: '20px',
        width: '100%',
        boxSizing: 'border-box'
    }
};

/**
 * Modal dialog displayed when meeting limit is exceeded.
 */
class MeetingLimitExceededDialog extends PureComponent<Props, State> {
    /**
     * Initializes a new component instance.
     *
     * @param {Props} props - The component props.
     */
    constructor(props: Props) {
        super(props);

        this.state = {
            isLeaving: false,
            isReloading: false,
            recheckError: null
        };

        this._hangupTimers = [];

        this._onHangup = this._onHangup.bind(this);
        this._onReload = this._onReload.bind(this);
        this._onUpgradePlan = this._onUpgradePlan.bind(this);
    }

    _hangupTimers: Array<TimeoutID>;

    /**
     * Implements React's {@link Component#componentWillUnmount()}.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        this._hangupTimers.forEach(clearTimeout);
        this._hangupTimers = [];
    }

    _onHangup: () => void;

    /**
     * Disconnects and leaves/ends the current meeting.
     *
     * @returns {void}
     */
    _onHangup() {
        if (this.state.isLeaving) {
            return;
        }

        this.setState({ isLeaving: true });

        const {
            _conference,
            _isModerator,
            _remoteParticipants,
            _rooms,
            _youtubeParticipant,
            dispatch,
            t
        } = this.props;

        // Only a moderator ends the meeting for everyone. For anyone else this button says "Leave
        // meeting" and must do exactly that - a non-moderator has no permission to kick, so running the
        // sequence below would only fire a burst of rejected IQs and then disconnect them anyway.
        if (!_isModerator) {
            dispatch(disconnect(false));

            return;
        }

        // Kick each remote participant, staggered. The delay is what the toolbar's own hang-up flow uses
        // (HangupContent.hitFunc): the kicks go out as individual IQs, and firing them in one tight loop
        // makes the burst easy for the server to rate-limit or drop, which leaves participants behind in
        // a meeting the moderator believes they ended.
        _remoteParticipants.forEach((participant, index) => {
            this._hangupTimers.push(setTimeout(
                () => dispatch(kickParticipant(participant.id, t('dialog.hangUpLeaveReason'))),
                index * 300));
        });

        // The shared-video participant is a fake participant, so it is excluded from the list above and
        // has to be removed separately or the video keeps playing for anyone still joining.
        if (_youtubeParticipant) {
            dispatch(kickParticipant(_youtubeParticipant.id));
        }

        // Breakout rooms outlive the main room, so anyone sitting in one would be left in a meeting that
        // no longer has a moderator.
        const breakoutRoomsManager = _conference?.getBreakoutRooms?.();

        if (breakoutRoomsManager) {
            Object.values(_rooms)
                .filter(room => !room.isMainRoom && room.jid)
                .forEach(room => breakoutRoomsManager.removeBreakoutRoom(room.jid));
        }

        // Leave only after the staggered kicks have all been sent, plus a margin for the last one to
        // reach the server. Disconnecting first would tear down our own XMPP connection with kicks still
        // queued on it, and those participants would simply stay in the room.
        const hangupDelay = (_remoteParticipants.length * 300) + 500;

        this._hangupTimers.push(setTimeout(() => {
            dispatch(disconnect(false));
            dispatch(updateConferenceCallEnded(true));
        }, hangupDelay));
    }

    _onReload: () => void;

    /**
     * Re-verifies meeting limits via totalparticipants API.
     *
     * @returns {void}
     */
    async _onReload() {
        if (this.state.isReloading) {
            return;
        }

        this.setState({
            isReloading: true,
            recheckError: null
        });

        const { dispatch, t } = this.props;

        const isAllowed = await dispatch(verifyConferenceMeetingLimit());

        if (isAllowed) {
            this.setState({
                isReloading: false,
                recheckError: null
            });
        } else {
            this.setState({
                isReloading: false,
                recheckError: t('conference.limitStillExceeded')
            });
        }
    }

    _onUpgradePlan: () => void;

    /**
     * Navigates user to subscription upgrade portal in a new tab.
     *
     * @returns {void}
     */
    _onUpgradePlan() {
        try {
            const url = getSubscriptionPage();

            if (typeof window !== 'undefined') {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        } catch (e) {
            // Ignore error
        }
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {React$Element<any>}
     */
    render() {
        const { _isModerator, _meetingLimitDetails, t } = this.props;
        const { isLeaving, isReloading, recheckError } = this.state;

        const limit = Number(_meetingLimitDetails?.allow_meeting_limit) || 1;
        const title = t('conference.meetingLimitExceededTitle');
        const desc = _isModerator
            ? t('conference.meetingLimitModeratorDesc', {
                limit,
                count: limit
            })
            : t('conference.meetingLimitParticipantDesc');

        const hangupLabel = isLeaving
            ? _isModerator ? t('conference.endingMeeting') : t('conference.leavingMeeting')
            : _isModerator ? t('conference.endMeetingBtn') : t('conference.leaveMeetingBtn');

        return (
            <div style = { styles.card }>
                <div style = { styles.iconWrapper }>
                    <svg
                        fill = 'none'
                        height = '34'
                        stroke = '#ED8936'
                        strokeLinecap = 'round'
                        strokeLinejoin = 'round'
                        strokeWidth = '2'
                        viewBox = '0 0 24 24'
                        width = '34'>
                        <path d = 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
                        <line
                            x1 = '12'
                            x2 = '12'
                            y1 = '9'
                            y2 = '13' />
                        <line
                            x1 = '12'
                            x2 = '12.01'
                            y1 = '17'
                            y2 = '17' />
                    </svg>
                </div>

                <div style = { styles.title }>{title}</div>

                <div style = { styles.description }>{desc}</div>

                {recheckError && <div style = { styles.alertBadge }>{recheckError}</div>}

                <div style = { styles.buttonContainer }>
                    {_isModerator && (
                        <button
                            onClick = { this._onUpgradePlan }
                            style = { styles.primaryButton }
                            type = 'button'>
                            <svg
                                fill = 'none'
                                height = '16'
                                stroke = 'currentColor'
                                strokeLinecap = 'round'
                                strokeLinejoin = 'round'
                                strokeWidth = '2'
                                viewBox = '0 0 24 24'
                                width = '16'>
                                <path d = 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' />
                                <polyline points = '15 3 21 3 21 9' />
                                <line
                                    x1 = '10'
                                    x2 = '21'
                                    y1 = '14'
                                    y2 = '3' />
                            </svg>
                            {t('conference.upgradePlanBtn')}
                        </button>
                    )}

                    {_isModerator && (
                        <button
                            disabled = { isReloading || isLeaving }
                            onClick = { this._onReload }
                            style = { isReloading ? styles.secondaryButtonDisabled : styles.secondaryButton }
                            type = 'button'>
                            <svg
                                fill = 'none'
                                height = '16'
                                stroke = 'currentColor'
                                strokeLinecap = 'round'
                                strokeLinejoin = 'round'
                                strokeWidth = '2'
                                style = { isReloading ? styles.spinnerIcon : undefined }
                                viewBox = '0 0 24 24'
                                width = '16'>
                                <polyline points = '23 4 23 10 17 10' />
                                <polyline points = '1 20 1 14 7 14' />
                                <path d = 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15' />
                            </svg>
                            {isReloading ? t('conference.reloading') : t('conference.reloadBtn')}
                        </button>
                    )}

                    <button
                        disabled = { isLeaving || isReloading }
                        onClick = { this._onHangup }
                        style = { isLeaving ? styles.dangerButtonDisabled : styles.dangerButton }
                        type = 'button'>
                        <Icon
                            size = { 16 }
                            src = { IconHangup } />
                        {hangupLabel}
                    </button>
                </div>
            </div>
        );
    }
}

/**
 * Maps (parts of) the Redux state to the associated props.
 *
 * @param {Object} state - The Redux state.
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const participants = state['features/base/participants'] || [];

    return {
        _conference: state['features/base/conference']?.conference,
        _isModerator: isLocalParticipantModerator(state),
        _meetingLimitDetails: state['features/base/conference']?.meetingLimitDetails || {},

        // Fake participants (the shared-video tile) are excluded here and kicked separately - they have
        // no XMPP session, so a staggered kick against them is wasted.
        _remoteParticipants: participants.filter(p => p.local !== true && !p?.isFakeParticipant),
        _rooms: state['features/breakout-rooms']?.rooms || {},
        _youtubeParticipant: getYoutubeParticipant(state)
    };
}

export default translate(connect(_mapStateToProps)(MeetingLimitExceededDialog));
